import { toUpper, toLower, startCase, trim, isArray, some, filter, map, uniq } from 'lodash'
import { formatPrice } from 'lib/utils'

export const calculateAjarleeAmount = (totalPrice, percentage) => {
  if (!totalPrice || !percentage) return 0
  return formatPrice(totalPrice * (percentage / 100))
}

export const hasSavedComponentDiscount = (bookingData, target) =>
  isArray(bookingData?.discountControl) && some(bookingData.discountControl, row => row?.target === target && Number(row?.value) > 0)

export const getEffectiveRentPrice = bookingData => {
  const discounted = Number(bookingData?.rentalCarDiscountPrice)
  if (bookingData?.rentalCarDiscountPrice != null && Number.isFinite(discounted) && (discounted > 0 || hasSavedComponentDiscount(bookingData, 'daily'))) {
    return discounted
  }
  return Number(bookingData?.priceWithoutTax || 0)
}

export const getPaidInsuranceAmount = bookingData => Number(bookingData?.insuranceFee || 0)

export const getAjarleePercentageBase = bookingData => {
  const rent = getEffectiveRentPrice(bookingData)
  const delivery = getOfficeDeliveryAmount(bookingData)
  const insurance = getPaidInsuranceAmount(bookingData)
  return rent + delivery + insurance
}

export const calculateBookingAjarleeAmount = bookingData => calculateAjarleeAmount(getAjarleePercentageBase(bookingData), bookingData?.ajarleePercentage)

export const isPickUpMyselfBooking = bookingData => {
  const type = toLower(trim(String(bookingData?.delivery?.type ?? ''))).replace(/[\s_-]/g, '')
  return type === 'pickupmyself'
}

export const getOfficeDeliveryAmount = bookingData => {
  if (isPickUpMyselfBooking(bookingData)) return 0

  const hasDeliveryDiscount = hasSavedComponentDiscount(bookingData, 'delivery')
  if (hasDeliveryDiscount && bookingData?.officeDiscountedDeliveryCost != null) {
    return Number(bookingData.officeDiscountedDeliveryCost) || 0
  }

  const companyAmount = Number(bookingData?.companyDeliveryAmount ?? 0)
  if (companyAmount > 0 || (hasDeliveryDiscount && bookingData?.companyDeliveryAmount != null)) return companyAmount

  const discountedOffice = Number(bookingData?.officeDiscountedDeliveryCost ?? 0)
  if (discountedOffice > 0) return discountedOffice

  const fullOffice = Number(bookingData?.officeDeliveryCost ?? 0)
  if (fullOffice > 0) return fullOffice

  const combined = Number(bookingData?.delivery?.deliveryDiscountCost || bookingData?.delivery?.deliveryCost || 0)
  const serviceFee = Number(bookingData?.ajarleeDeliveryCost || 0)
  return Math.max(0, combined - serviceFee)
}

export const getAjarleeServiceFee = bookingData => {
  if (isPickUpMyselfBooking(bookingData)) return 0
  if (hasSavedComponentDiscount(bookingData, 'service') && bookingData?.ajarleeDiscountedDeliveryCost != null) {
    return Number(bookingData.ajarleeDiscountedDeliveryCost) || 0
  }
  if (bookingData?.ajarleeDeliveryAmount != null) return Number(bookingData.ajarleeDeliveryAmount) || 0
  const discounted = Number(bookingData?.ajarleeDiscountedDeliveryCost ?? 0)
  if (discounted > 0) return discounted
  return Number(bookingData?.ajarleeDeliveryCost || 0)
}

export const getExtrasAmount = bookingData => Number(bookingData?.extrasAmount ?? (bookingData?.extras || []).reduce((sum, extra) => sum + Number(extra?.amount || 0), 0)) || 0

export const getCustomerBookingPaymentTotal = bookingData => {
  const rent = getEffectiveRentPrice(bookingData)
  const officeDelivery = getOfficeDeliveryAmount(bookingData)
  const serviceFee = getAjarleeServiceFee(bookingData)
  const insurance = Number(bookingData?.insuranceFee || 0)
  const extras = getExtrasAmount(bookingData)
  const vat = Number(bookingData?.vatTax || 0)
  return rent + officeDelivery + serviceFee + insurance + extras + vat
}

export const resolvePaymentDeliveryBreakdown = ({ bookingData, priceData, deliveryCost }) => {
  if (isPickUpMyselfBooking(bookingData)) {
    return { officeDelivery: 0, serviceFee: 0, combinedDelivery: 0 }
  }

  const combined = Number(priceData?.deliveryCost ?? deliveryCost ?? bookingData?.delivery?.deliveryDiscountCost ?? bookingData?.delivery?.deliveryCost ?? 0)
  const serviceFee = Number(priceData?.ajarleeDeliveryCost ?? bookingData?.ajarleeDeliveryCost ?? 0)
  const officeDelivery = Number(priceData?.officeDeliveryCost ?? bookingData?.companyDeliveryAmount ?? Math.max(0, combined - serviceFee))
  return { officeDelivery, serviceFee, combinedDelivery: combined }
}

const PRICE_EPSILON = 0.0005

export const getSavedDiscountRows = (bookingData, target) =>
  isArray(bookingData?.discountControl) ? filter(bookingData.discountControl, row => row?.target === target && Number(row?.value) > 0) : []

export const getDiscountSourcesForTarget = (bookingData, target) => uniq(map(getSavedDiscountRows(bookingData, target), row => trim(String(row?.source ?? '')))).filter(Boolean)

const buildComponentDiscount = (bookingData, target, original, discounted, forceHasDiscount = false) => {
  const originalAmount = Number(original || 0)
  const discountedAmount = Number(discounted || 0)
  const sources = getDiscountSourcesForTarget(bookingData, target)
  const amount = Math.max(0, originalAmount - discountedAmount)
  return {
    hasDiscount: (forceHasDiscount || sources.length > 0) && amount > PRICE_EPSILON,
    original: originalAmount,
    discounted: discountedAmount,
    amount,
    sources,
  }
}

export const getBookingDiscountBreakdown = bookingData => ({
  daily: buildComponentDiscount(bookingData, 'daily', bookingData?.priceWithoutTax, getEffectiveRentPrice(bookingData), hasRentPriceDiscount(bookingData)),
  delivery: buildComponentDiscount(bookingData, 'delivery', bookingData?.officeDeliveryCost, getOfficeDeliveryAmount(bookingData)),
  service: buildComponentDiscount(bookingData, 'service', bookingData?.ajarleeDeliveryCost, getAjarleeServiceFee(bookingData)),
})

export const getBookingTotalSummary = bookingData => {
  const componentTotal = getCustomerBookingPaymentTotal(bookingData) || Number(bookingData?.totalPrice || 0)
  const storedTotal = Number(bookingData?.totalPrice ?? NaN)
  const sources = getDiscountSourcesForTarget(bookingData, 'total')
  const hasStoredTotal = Number.isFinite(storedTotal)

  const paidInsurance = getPaidInsuranceAmount(bookingData)
  const storedExcludesPaidInsurance = hasStoredTotal && paidInsurance > 0 && sources.length === 0 && Math.abs(componentTotal - (storedTotal + paidInsurance)) <= PRICE_EPSILON
  const storedIsLower = hasStoredTotal && !storedExcludesPaidInsurance && storedTotal < componentTotal - PRICE_EPSILON
  const hasTotalDiscount = storedIsLower && (sources.length > 0 || storedTotal > 0)

  const total = hasTotalDiscount ? storedTotal : componentTotal
  const extendOriginal = Number(bookingData?.extendBookingPreDiscountTotalPrice ?? NaN)
  const original = hasTotalDiscount ? componentTotal : Number.isFinite(extendOriginal) && extendOriginal > total + PRICE_EPSILON ? extendOriginal : null

  return {
    hasDiscount: original != null,
    original: original ?? total,
    discounted: total,
    amount: original != null ? Math.max(0, original - total) : 0,
    sources,
  }
}

export const hasRentPriceDiscount = bookingData => {
  const priceWithoutTax = Number(bookingData?.priceWithoutTax ?? 0)
  const rentalCarDiscountPrice = Number(bookingData?.rentalCarDiscountPrice ?? 0)
  const hasExtendDiscount = Number(bookingData?.extendBookingAjarleeDiscountPercentage ?? 0) > 0
  const isFullyDiscountedRent = bookingData?.rentalCarDiscountPrice != null && rentalCarDiscountPrice === 0 && hasSavedComponentDiscount(bookingData, 'daily')

  return hasExtendDiscount || (priceWithoutTax > 0 && (rentalCarDiscountPrice > 0 || isFullyDiscountedRent) && priceWithoutTax !== rentalCarDiscountPrice)
}

export const isExtendedBooking = bookingData => {
  const ref = trim(String(bookingData?.bookingReferenceId || ''))
  if (ref && /^.+-\d+$/.test(ref)) return true
  return Number(bookingData?.extendBookingAjarleeDiscountPercentage ?? 0) > 0
}

export const getStatusTranslation = (t, status) => {
  return t(`booking.status.${status}`) || toUpper(status)
}

export const getPaymentStatusTranslation = (t, paymentStatus) => {
  return t(`booking.paymentStatus.${paymentStatus}`) || toUpper(paymentStatus)
}

export const getCreatedByTranslation = (t, createdBy) => {
  return t(`booking.createdBy.${toLower(createdBy)}`) || toUpper(createdBy)
}

export const getPaymentMethodTranslation = (t, paymentMethod) => {
  if (!paymentMethod) return ''
  const key = toLower(String(paymentMethod))
  const bookingKey = `booking.paymentMethod.${key}`
  const bookingLabel = t(bookingKey)
  if (bookingLabel !== bookingKey) return bookingLabel
  const bookingsKey = `bookings.paymentMethod.${key}`
  const bookingsLabel = t(bookingsKey)
  if (bookingsLabel !== bookingsKey) return bookingsLabel
  return startCase(key)
}

const CARD_GATEWAYS = { paymob: 'Paymob', thawani: 'Thawani', qpay: 'QPay' }

export const withCardGateway = (label, booking) => {
  const gateway = toLower(booking?.paymentMethod) === 'card' && CARD_GATEWAYS[toLower(booking?.paymentSessionMetadata?.gateway)]
  return gateway ? `${label} (${gateway})` : label
}

export const getDiscountSourceLabel = (t, source) => {
  const key = toLower(trim(source || ''))
  if (key === 'ajarlee' || key === 'ajrlee') return t('booking.discountSource.ajarlee')
  if (key === 'company') return t('booking.discountSource.company')
  if (!key || key === 'none') return t('booking.discountSource.none')
  const translated = t(`booking.discountSource.${key}`)
  return translated !== `booking.discountSource.${key}` ? translated : startCase(String(source))
}

export const getDiscountSourceTranslation = (t, source) => toUpper(getDiscountSourceLabel(t, source))

export const getDiscountSourceBadgeClass = source => {
  const key = toLower(trim(String(source || '')))
  if (key === 'ajarlee' || key === 'ajrlee') return 'bg-emerald-100 text-emerald-800 ring-emerald-200/60'
  if (key === 'company') return 'bg-sky-100 text-sky-800 ring-sky-200/60'
  return 'bg-slate-200 text-slate-700 ring-slate-300/50'
}

export const getBookingCoupon = bookingData => {
  const discount = bookingData?.discount
  // eslint-disable-next-line
  if (!discount || typeof discount !== 'object') return null
  const code = trim(String(discount.cupon ?? ''))
  if (!code) return null
  return discount
}

export const getCouponCode = discount => trim(String(discount?.cupon ?? ''))

export const isPercentageCoupon = discount => toLower(String(discount?.type ?? '')) === 'percentage'

export const getCouponTypeTranslation = (t, discount) => {
  const typeLower = toLower(String(discount?.type ?? ''))
  if (typeLower === 'percentage') return t('booking.coupon.percentage')
  if (typeLower === 'flat') return t('booking.coupon.flat')
  return startCase(String(discount?.type ?? ''))
}

export const getCouponValueDisplay = (t, discount) => {
  if (isPercentageCoupon(discount)) {
    const p = Number(discount?.percentage)
    return `${Number.isFinite(p) ? p : 0}%`
  }
  const a = Number(discount?.amount)
  return `${formatPrice(Number.isFinite(a) ? a : 0, true)} ${t('common.omr')}`
}

export const resolveBookingSourceKey = (bookingSource, platform) => {
  const source = toLower(trim(String(bookingSource || '')))

  if (platform === 'iOS App' || platform === 'Android App') return 'app'
  if (platform === 'Website' || source === 'website' || source === 'web') return 'website'
  if (source === 'mobile' || source === 'mobile_app') return 'app'

  return null
}

export const getBookingSourceTranslation = (t, bookingSource, platform) => {
  const key = resolveBookingSourceKey(bookingSource, platform)
  if (!key) return '-'

  const translated = t(`bookings.bookingSource.${key}`)
  return translated !== `bookings.bookingSource.${key}` ? translated : startCase(key)
}

export const getPlatformTranslation = (t, platform) => {
  if (!platform) return '-'
  const key = platform === 'Website' ? 'browser' : platform === 'iOS App' ? 'ios' : platform === 'Android App' ? 'android' : null
  if (key) {
    const translated = t(`booking.platform.${key}`)
    if (translated !== `booking.platform.${key}`) return translated
  }
  return platform
}

export const getDeliveryTypeTranslation = (t, deliveryType) => {
  const normalizedType = toLower(deliveryType || '').replace(/[\s_-]/g, '')

  if (!normalizedType) {
    return ''
  }

  if (normalizedType === 'pickupmyself') {
    return t('booking.deliveryType.pickUpMyself')
  }

  if (normalizedType === 'delivertome') {
    return t('booking.deliveryType.deliverToMe')
  }

  const translationKey = `booking.deliveryType.${normalizedType}`
  const translatedValue = t(translationKey)

  if (translatedValue !== translationKey) {
    return translatedValue
  }

  return startCase(deliveryType)
}

export const getAcknowledgmentStatusTranslation = (t, status) => {
  if (status === 'handover') {
    return t('booking.acknowledgmentStatus.handover')
  }
  if (status === 'not handover') {
    return t('booking.acknowledgmentStatus.notHandover')
  }
  return toUpper(status.replace('_', ' '))
}

export const getInsuranceDisplay = bookingData => {
  const type = bookingData?.insuranceType || 'NONE'
  const hasSplitType = type === 'STANDARD' || type === 'FULL'
  const included = !!bookingData?.isInsuranceIncludedInBookingPayment
  const amount = Number(bookingData?.insuranceAmount || 0)
  const fee = Number(bookingData?.insuranceFee || 0)
  const collectAtOffice = hasSplitType && !included
  return {
    type,
    hasSplitType,
    included,
    collectAtOffice,
    // Office-collected shows the full amount (fee is 0); online shows the paid fee.
    displayAmount: collectAtOffice ? amount : fee,
    // Render the row for split-type bookings, or legacy bookings that carried a fee.
    show: hasSplitType || fee > 0,
  }
}

/** Localized label for the selected insurance type (Standard / Full), or '' for none. */
export const getInsuranceTypeLabel = (bookingData, t) => {
  const type = bookingData?.insuranceType
  if (type === 'STANDARD') return t('booking.orderSummary.insuranceStandard')
  if (type === 'FULL') return t('booking.orderSummary.insuranceFull')
  return ''
}

/**
 * Insurance for the Edit Booking screen — mirrors the API's repriceBookingInsurance: an unchanged type
 * keeps the booked unit price / payment flag, a newly chosen type takes the car's price and the company
 * flag. `fee` is the payable slice that enters VAT + total (0 when collected at the office).
 */
export const getEditInsurance = (bookingData, type, days) => {
  const oldType = bookingData?.insuranceType || 'NONE'
  const same = type === oldType
  if (type === 'NONE') {
    const fee = same ? Number(bookingData?.insuranceFee || 0) : 0
    return { type, amount: fee, fee, unitPrice: 0, collectAtOffice: false }
  }
  const car = bookingData?.rentalCar
  const isFull = type === 'FULL'
  const unitPrice =
    same && bookingData?.insuranceUnitPrice != null ? Number(bookingData.insuranceUnitPrice) : Number((isFull ? car?.fullInsurancePricePerDay : car?.insuranceFee) || 0)
  const included =
    same && bookingData?.isInsuranceIncludedInBookingPayment != null
      ? !!bookingData.isInsuranceIncludedInBookingPayment
      : !!(isFull ? car?.company?.includeFullInsuranceInBookingPayment : car?.company?.includeStandardInsuranceInBookingPayment)
  const amount = Math.round((isFull ? unitPrice * days : unitPrice) * 1000) / 1000
  return { type, amount, fee: included ? amount : 0, unitPrice, collectAtOffice: !included }
}
